import './lists.css';
import * as React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Button, ThemeProvider } from '@workboard/wobo-ui';
import { MenuComponent } from './menu.component';
import { getLists, getList } from './listHelpers/lists.api';
import { usePendoVisitorReport } from '../../hooks/usePendoVisitorReport';
import  { useTranslation } from 'react-i18next';

const subActive = window.wbConfig.scheduledBriefsEnabled;

const styledColumnHeader = {
  fontFamily: 'Lato',
  fontSize: '12px',
  fontWeight: 'bold',
  lineHeight: '16px',
  letterSpacing: '0.15000000596046448px',
  paddingBottom: '15px',
  paddingTop: '20px',
  paddingLeft: '0px',
};

const styledCell = {
  fontFamily: 'Lato',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  letterSpacing: '0.15000000596046448px',
  textAlign: 'left',
  color: '#333943',
  paddingLeft: '0px',
};

const styledSubDiv = {
  fontFamily: 'Lato',
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '16px',
  letterSpacing: '0.15000000596046448px',
  textAlign: 'left',
  color: '#687587',
  paddingTop: '5px',
};
const imageSrc = {
  0: '/images/subscription/uparrow.svg',
  1: '/images/subscription/list.svg',
  2: '/images/subscription/list.svg',
  3: '/images/subscription/bizreview.svg?v=1',
  4: '/images/subscription/scorecard.svg?v=1',
  5: '/images/subscription/list.svg',
};

const RESOURCE_TYPE_BIZREVIEW = '3';
const RESOURCE_TYPE_SCORECARD = '4';

export const ListsComp = (props) => {
  const { t } = useTranslation();
  const rows = [10, 25, 100];
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [deleted, setDeleted] = React.useState(null);
  const [updatedData, setUpdatedData] = React.useState(null);
  const [unsubscribed, setUnsubscribed] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(rows[1]);
  const columns = [
    {
      id: 'logo',
      label: wbConfig.baseUrl + imageSrc[0],
      headerStyle: { ...styledColumnHeader },
      cellStyle: { ...styledCell, fontWeight: 'bold', width: '5%' },
      isHtml: true,
    },
    {
      id: 'name',
      label: t('Name'),
      headerStyle: { ...styledColumnHeader },
      cellStyle: { ...styledCell, fontWeight: 'bold', width: '30%' },
    },
    {
      id: 'owner',
      label: t('Owner'),
      headerStyle: { ...styledColumnHeader },
      cellStyle: { ...styledCell, width: subActive ? '20%' : '30%' },
    },
    {
      id: 'modified',
      label: t('Modified'),
      headerStyle: { ...styledColumnHeader },
      cellStyle: { ...styledCell, width: subActive ? '20%' : '30%' },
    },
    {
      id: 'subscriptions',
      label: t('Subscriptions'),
      headerStyle: { ...styledColumnHeader },
      cellStyle: { ...styledCell, width: '30%' },
    },
    {
      id: 'actions',
      label: t('Actions'),
      headerStyle: { ...styledColumnHeader },
      cellStyle: { ...styledCell },
    },
  ];
  const handleCreateList = () => {
    window?.initListBuilderComponent('list-dialog', setUpdatedData, null, null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  usePendoVisitorReport(props.data.isPendoEnabled);

  //analytics
  React.useEffect(() => {
    window.wb_track('LIST_EVENT', {
      eventType: 'VIEW',
      targetItem: 'lists',
      resourceType: 'list',
      location: 'lists_landing_page',
    });
  }, []);

  //fetch lists
  React.useEffect(() => {
    const fetchLists = async () => {
      try {
        await getLists(rows[2], setData, setLoading);
      } catch (error) {
        console.error(error);
      }
    };

    fetchLists();
  }, []);

  //remove deleted list from data
  React.useEffect(() => {
    if (!deleted) return;
    const removedDeleted = data.filter((item) => item.id !== deleted);
    setData(removedDeleted);
    setDeleted(null);
  }, [deleted]);

  //unsubscribe from list
  React.useEffect(() => {
    if (!unsubscribed) return;
    const removedUnsubscribed = data.filter((item) => item.id !== unsubscribed);
    setData(removedUnsubscribed);
    setUnsubscribed(null);
  }, [unsubscribed]);

  //update list data
  React.useEffect(() => {
    if (!updatedData) {
      return;
    }

    const fetchList = async () => {
      try {
        await getList(updatedData, data, setData, setUpdatedData);
      } catch (error) {
        console.error(error);
      }
    };

    fetchList();
  }, [updatedData]);

  //loading state
  if (loading) {
    return <div></div>;
  }

  return (
    <>
      <ThemeProvider>
        <div className="lists-comp">
          <div className="lists-header">
            <div className="lists-header-title">{t('Lists & Subscriptions')}</div>
            <div>
              <Button
                className="list-new-button"
                variant="primary"
                size="large"
                startIcon={<Add />}
                onClick={handleCreateList}
              >
                {t('New list')}
              </Button>
            </div>
          </div>

          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => {
                    if (column.id == 'subscriptions' && !subActive) return null;
                    return (
                      <TableCell key={column.id} sx={{ ...column.headerStyle, width: column.cellStyle.width }}>
                        { (column.id == 'logo') ? <img src={column.label} /> : column.label}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 &&
                  data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    return (
                      <TableRow>
                        {columns.map((column) => {
                          let value = row[column.id];
                          const typeId = Number(row['typeId']);
                          if (column.id == 'subscriptions' && !subActive) return null;
                          if (column.id == 'subscriptions') {
                            const lines = value.split('\n');
                            return (
                              <TableCell sx={column.cellStyle} key={column.id}>
                                {lines.map((line, index) => {
                                  if (index == 0) {
                                    return <div key={index}>{line}</div>;
                                  }
                                  return (
                                    <div style={styledSubDiv} key={index}>
                                      {line}
                                    </div>
                                  );
                                })}
                              </TableCell>
                            );
                          } else if (column.id == 'actions') {
                            return (
                            <TableCell sx={column.cellStyle} key={row.id}>
                                <MenuComponent list={row} setDeleted={setDeleted} setUpdateData={setUpdatedData} setUnsubscribed={setUnsubscribed} listOwner={row.listOwner} resourceType={row.typeName}/>
                              </TableCell>
                            );
                          }  else if (column.id == 'logo') {
                            return (
                              <TableCell sx={column.cellStyle} key={column.id}>
                                <img src={wbConfig.baseUrl + imageSrc[typeId] }  />
                              </TableCell>
                            );
                          } else if ((row.typeId == RESOURCE_TYPE_BIZREVIEW  || row.typeId == RESOURCE_TYPE_SCORECARD) && column.id == 'name') {
                            return (
                              <TableCell sx={column.cellStyle} key={column.id}>
                                {row.contents[0]?.description??row.name}
                              </TableCell>
                            );
                          } else {
                            return (
                              <TableCell sx={column.cellStyle} key={column.id}>
                                {value}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })}
                {data.length <= 0 && (
                  //handles empty page state
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <div className="lists-empty-content">
                        <img src={wbConfig.baseUrl + '/images/empty_lists.png'} alt="Empty lists" />
                        <p className="lists-empty-text">{t('Create your first list')}!</p>
                        <p className="lists-empty-sub-text">
                        {t("Seems like you haven't made any lists yet. Creating lists helps you get notifications")}<br />
                        {t('about what matters to you and keeps your team informed')}.
                        </p>
                        <Button
                          className="list-empty-new-button"
                          variant="primary"
                          size="large"
                          startIcon={<Add />}
                          onClick={handleCreateList}
                        >
                          {t('New list')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {data.length > 0 && (
            <TablePagination
              rowsPerPageOptions={rows}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </div>
      </ThemeProvider>
      <div id="list-dialog" />
    </>
  );
};
